/**
 * Composable that provides authenticated `$fetch` helpers.
 *
 * Centralises the auth-header construction and "Invalid Token" handling
 * that was previously copy-pasted across every store.
 */
export function useAuthenticatedFetch() {
  const authStore = useAuthStore();
  const config = useRuntimeConfig();

  /** Build the standard auth headers for customer API calls. */
  function authHeaders(): Record<string, string> {
    return {
      accept: "application/json",
      "x-access-token-customer": authStore.token!.token,
      "content-type": "application/json",
    };
  }

  /** Whether the user has a valid token. */
  function hasToken(): boolean {
    return !!authStore.token?.token;
  }

  /**
   * Authenticated $fetch wrapper.
   *
   * @param path  - API path (appended to `apiBase`)
   * @param opts  - $fetch options (method, body, etc.) – auth headers are
   *                merged automatically.
   * @returns The typed response from the API.
   */
  async function authFetch<T>(path: string, opts: Record<string, unknown> = {}): Promise<T> {
    return $fetch<T>(`${config.public.apiBase}${path}`, {
      ...opts,
      headers: {
        ...authHeaders(),
        ...((opts.headers as Record<string, string>) || {}),
      },
    });
  }

  return { authHeaders, authFetch, hasToken, apiBase: config.public.apiBase };
}
